// Sprint 14.0 — Panel de Periféricos: configura la impresora del dispositivo (persistente en localStorage)
// y permite imprimir un ticket de prueba por el motor híbrido ESC/POS.
import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Printer, Bluetooth, Server, Smartphone, Usb, Plug, TestTube2, Loader2, CheckCircle2, Download, RefreshCw, Wifi, WifiOff } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  loadPrinterConfig, savePrinterConfig, buildTestTicket, printBuffer,
  printNativePopup, buildTestNativeBody, detectPlatform,
  requestUsbDevice, printViaWebUSB, usbSupported, fetchLocalPrinters, pingLocalAgent, buildTicketPreview,
} from "@/lib/escposHelper";

// URL de descarga del agente compilado (configurable por entorno; fallback a ruta estática del frontend).
const AGENT_URL = process.env.REACT_APP_PRINT_AGENT_URL || "/agent/ParkNovaPrintAgent.exe";

const MODOS = [
  { v: "bluetooth", label: "Bluetooth (RawBT / Android)", icon: Bluetooth },
  { v: "usb", label: "USB Directo (Cable PC / POS)", icon: Usb },
  { v: "local", label: "Servicio Local (PC)", icon: Server },
  { v: "nativo", label: "Nativo / AirPrint (iOS / Fallback)", icon: Smartphone },
];

export default function ConfiguracionImpresoraModal({ open, onClose }) {
  const [cfg, setCfg] = useState(loadPrinterConfig());
  const [testing, setTesting] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const [localPrinters, setLocalPrinters] = useState([]);
  const [detectando, setDetectando] = useState(false);
  const [agentStatus, setAgentStatus] = useState("unknown"); // unknown | checking | online | offline
  const qrRef = useRef(null);
  const preview = buildTicketPreview(cfg);

  useEffect(() => { if (open) setCfg(loadPrinterConfig()); }, [open]);

  // Verifica el healthcheck del agente local en segundo plano y actualiza la insignia.
  const checkAgent = async (c = cfg) => {
    setAgentStatus("checking");
    const ok = await pingLocalAgent({ host: c.host, port: c.port });
    setAgentStatus(ok ? "online" : "offline");
    return ok;
  };

  // Al abrir el modal o al cambiar a modo "local", dispara la verificación automática.
  useEffect(() => {
    if (open && cfg.modo === "local") { checkAgent(); }
    else if (cfg.modo !== "local") { setAgentStatus("unknown"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cfg.modo, cfg.host, cfg.port]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const vincularUsb = async () => {    setVinculando(true);
    try {
      const device = await requestUsbDevice();
      const name = device.productName || device.manufacturerName || `USB ${device.vendorId}:${device.productId}`;
      const next = { ...cfg, usbVendorId: device.vendorId, usbProductId: device.productId, usbName: name };
      setCfg(next);
      savePrinterConfig(next); // el permiso ya está otorgado; recordamos el dispositivo
      toast.success(`Impresora USB vinculada: ${name}`);
    } catch (e) {
      toast.error(e?.message || "No se pudo vincular la impresora USB");
    } finally {
      setVinculando(false);
    }
  };

  const guardar = () => {
    savePrinterConfig(cfg);
    toast.success("Configuración de impresora guardada en este dispositivo");
    onClose?.();
  };

  // Consulta el agente local (localhost:8080) para listar impresoras del spooler + puertos COM/Bluetooth.
  const detectarImpresoras = async () => {
    setDetectando(true);
    try {
      const list = await fetchLocalPrinters({ host: cfg.host, port: cfg.port });
      setLocalPrinters(list);
      setAgentStatus("online");
      if (list.length === 0) toast.warning("El agente no encontró impresoras. Verifica que esté ejecutándose.");
      else toast.success(`${list.length} impresora(s) detectada(s)`);
    } catch (e) {
      setAgentStatus("offline");
      toast.error(e?.message || "No se pudo contactar al agente en http://127.0.0.1:8080. Descárgalo y ábrelo primero.");
    } finally {
      setDetectando(false);
    }
  };

  const imprimirPrueba = async () => {
    savePrinterConfig(cfg); // recuerda antes de probar
    setTesting(true);
    try {
      if (cfg.modo === "nativo") {
        const canvas = qrRef.current?.querySelector("canvas");
        const qrDataUrl = canvas ? canvas.toDataURL("image/png") : "";
        printNativePopup({ papel: cfg.papel, qrDataUrl, title: "PARKNOVA - PRUEBA", body: buildTestNativeBody(cfg.papel, qrDataUrl) });
        toast.success("Ticket de prueba enviado (Nativo / AirPrint)");
      } else if (cfg.modo === "usb") {
        try {
          await printViaWebUSB(buildTestTicket(cfg), cfg);
          toast.success("Ticket de prueba enviado por USB Directo");
        } catch (e) {
          // Fallback silencioso a impresión nativa: el usuario nunca se queda sin ticket
          const canvas = qrRef.current?.querySelector("canvas");
          const qrDataUrl = canvas ? canvas.toDataURL("image/png") : "";
          printNativePopup({ papel: cfg.papel, qrDataUrl, title: "PARKNOVA - PRUEBA", body: buildTestNativeBody(cfg.papel, qrDataUrl) });
          toast.warning("USB no disponible; se usó impresión nativa como respaldo");
        }
      } else {
        const bytes = buildTestTicket(cfg);
        await printBuffer(bytes, cfg);
        toast.success(cfg.modo === "bluetooth" ? "Enviado a RawBT (Bluetooth)" : "Enviado al servicio local (PC)");
      }
    } catch (e) {
      toast.error(e?.message || "No se pudo imprimir el ticket de prueba");
    } finally {
      setTesting(false);
    }
  };

  const plat = detectPlatform();
  const sugerido = plat.android ? "Bluetooth" : plat.iOS ? "Nativo / AirPrint" : "USB Directo (Cable PC / POS)";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-none border-[#FFCC00]/40 bg-[#0A0A0A] text-white max-w-md w-[92vw] sm:w-full max-h-[90dvh] overflow-y-auto overflow-x-hidden" data-testid="printer-config-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-white flex items-center gap-2">
            <Printer className="h-5 w-5 text-[#FFCC00]" /> Configurar impresora
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Preferencias guardadas solo en este dispositivo. Sugerido aquí: <span className="text-[#FFCC00] font-bold">{sugerido}</span>.
          </DialogDescription>
        </DialogHeader>

        {cfg.modo === "local" && (
          <div data-testid="agent-status-badge" data-status={agentStatus}
            className={`flex items-center justify-between gap-2 px-3 py-2 border text-xs font-bold ${
              agentStatus === "online"
                ? "border-[#10B981] bg-[#10B981]/10 text-[#10B981]"
                : agentStatus === "checking"
                ? "border-white/20 bg-white/5 text-white/70"
                : "border-[#EF4444] bg-[#EF4444]/10 text-[#EF4444]"
            }`}>
            <span className="flex items-center gap-2">
              {agentStatus === "checking" ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Verificando agente…</>
              ) : agentStatus === "online" ? (
                <><Wifi className="h-4 w-4" /> Agente Conectado ({cfg.host}:{cfg.port})</>
              ) : (
                <><WifiOff className="h-4 w-4" /> Agente Desconectado</>
              )}
            </span>
            <span className="flex items-center gap-3">
              <button type="button" onClick={() => checkAgent()} title="Reintentar" data-testid="agent-status-recheck"
                className="opacity-70 hover:opacity-100"><RefreshCw className="h-3.5 w-3.5" /></button>
              {agentStatus === "offline" && (
                <a href={AGENT_URL} download="ParkNovaPrintAgent.exe" data-testid="agent-status-download"
                  className="underline decoration-dotted hover:opacity-80">Descargar Agente</a>
              )}
            </span>
          </div>
        )}

        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label className="text-[10px] tracking-[0.1em] uppercase font-bold text-white/60">Modo de conexión</Label>
            <Select value={cfg.modo} onValueChange={(v) => set({ modo: v })}>
              <SelectTrigger data-testid="printer-modo" className="h-11 rounded-none bg-white/5 border-white/20 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] text-white border-white/20">
                {MODOS.map((m) => (
                  <SelectItem key={m.v} value={m.v} data-testid={`printer-modo-${m.v}`} className="focus:bg-[#FFCC00]/10">
                    <span className="flex items-center gap-2"><m.icon className="h-4 w-4 text-[#FFCC00]" /> {m.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[10px] tracking-[0.1em] uppercase font-bold text-white/60">Tamaño de papel</Label>
            <div className="grid grid-cols-2 gap-2">
              {["58", "80"].map((p) => (
                <button key={p} type="button" onClick={() => set({ papel: p })} data-testid={`printer-papel-${p}`}
                  className={`h-11 border text-sm font-bold uppercase tracking-wider transition-colors ${cfg.papel === p ? "bg-[#FFCC00] text-[#0A0A0A] border-[#FFCC00]" : "border-white/20 text-white/70 hover:border-[#FFCC00]/60"}`}>
                  {p}mm
                </button>
              ))}
            </div>
          </div>

          {/* Vista previa del ticket según el ancho de papel seleccionado (32 vs 48 caracteres). */}
          <div className="space-y-1.5" data-testid="printer-preview">
            <Label className="text-[10px] tracking-[0.1em] uppercase font-bold text-white/60">
              Vista previa ({cfg.papel === "58" ? "32" : "48"} caracteres · {cfg.papel}mm)
            </Label>
            <div className="flex justify-center bg-white/5 border border-white/10 p-3">
              <pre data-testid="printer-preview-pre"
                className="bg-white text-black text-[11px] leading-[1.35] font-mono px-2 py-3 shadow-md whitespace-pre overflow-x-auto"
                style={{ width: `${preview.cols}ch`, letterSpacing: 0 }}>
{preview.lines.join("\n")}
              </pre>
            </div>
          </div>

          {cfg.modo === "usb" && (
            <div className="space-y-2" data-testid="printer-usb-section">
              {cfg.usbName ? (
                <div className="flex items-center gap-2 border border-[#10B981]/40 bg-[#10B981]/10 px-3 py-2" data-testid="printer-usb-status">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#10B981] shadow-[0_0_8px_#10B981]" />
                  <CheckCircle2 className="h-4 w-4 text-[#10B981]" />
                  <span className="text-sm text-white truncate">{cfg.usbName}</span>
                </div>
              ) : (
                <p className="text-xs text-white/50">Vincula tu impresora USB una sola vez; el navegador recordará el permiso.</p>
              )}
              <button type="button" onClick={vincularUsb} disabled={vinculando || !usbSupported()} data-testid="printer-usb-vincular"
                className="btn-hard w-full h-11 border border-[#FFCC00] text-[#FFCC00] font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#FFCC00]/10 disabled:opacity-60">
                {vinculando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                {cfg.usbName ? "Revincular impresora USB" : "Vincular impresora USB"}
              </button>
              {!usbSupported() && (
                <p className="text-xs text-[#EF4444]" data-testid="printer-usb-unsupported">WebUSB no está disponible en este navegador. Usa Chrome/Edge en PC.</p>
              )}
            </div>
          )}

          {cfg.modo === "local" && (
            <div className="space-y-3" data-testid="printer-local-section">
              {/* Descarga del agente Plug & Play (fuerza descarga directa del .exe) */}
              <div className="border border-[#FFCC00]/40 bg-[#FFCC00]/5 p-3 space-y-2">
                <p className="text-xs text-white/70 leading-relaxed">
                  Instala el <span className="font-bold text-[#FFCC00]">Agente de Impresión</span> en esta PC para imprimir por
                  USB, red o <span className="font-bold text-[#FFCC00]">Bluetooth (puerto COM)</span>. Sin Python ni consolas.
                </p>
                <a href={AGENT_URL} download="ParkNovaPrintAgent.exe" data-testid="printer-download-agent"
                  className="btn-hard w-full h-11 bg-[#FFCC00] text-[#0A0A0A] border border-[#FFCC00] font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#e6b800]">
                  <Download className="h-4 w-4" /> Descargar Agente
                </a>
              </div>

              {/* Detección + selección de impresora (spooler o COM/Bluetooth) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] tracking-[0.1em] uppercase font-bold text-white/60">Impresora del agente</Label>
                  <button type="button" onClick={detectarImpresoras} disabled={detectando} data-testid="printer-detect-btn"
                    className="text-[11px] font-bold uppercase tracking-wider text-[#FFCC00] flex items-center gap-1 hover:opacity-80 disabled:opacity-50">
                    {detectando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Detectar impresoras
                  </button>
                </div>
                <Select value={cfg.printerName || ""} onValueChange={(v) => set({ printerName: v })}>
                  <SelectTrigger data-testid="printer-name-select" className="h-11 rounded-none bg-white/5 border-white/20 text-white">
                    <SelectValue placeholder="Selecciona una impresora detectada" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] text-white border-white/20">
                    {localPrinters.map((p) => (
                      <SelectItem key={p.name} value={p.name} data-testid={`printer-name-${p.name}`} className="focus:bg-[#FFCC00]/10">
                        <span className="flex items-center gap-2">
                          {p.type === "serial" ? <Bluetooth className="h-4 w-4 text-[#FFCC00]" /> : <Printer className="h-4 w-4 text-[#FFCC00]" />}
                          {p.name}{p.type === "serial" ? " · COM/Bluetooth" : ""}{p.is_default ? " · (predeterminada)" : ""}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cfg.printerName && (
                  <p className="text-xs text-[#10B981] flex items-center gap-1.5" data-testid="printer-name-selected">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Seleccionada: {cfg.printerName}
                  </p>
                )}
              </div>

              {/* Conexión avanzada del agente (host/puerto) */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-[10px] tracking-[0.1em] uppercase font-bold text-white/60">Host</Label>
                  <input value={cfg.host} onChange={(e) => set({ host: e.target.value })} data-testid="printer-host"
                    className="h-11 w-full rounded-none bg-white/5 border border-white/20 text-white px-3 focus:outline-none focus:ring-2 focus:ring-[#FFCC00]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] tracking-[0.1em] uppercase font-bold text-white/60">Puerto</Label>
                  <input type="number" value={cfg.port} onChange={(e) => set({ port: Number(e.target.value) || 8080 })} data-testid="printer-port"
                    className="h-11 w-full rounded-none bg-white/5 border border-white/20 text-white px-3 focus:outline-none focus:ring-2 focus:ring-[#FFCC00]" />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2 pt-1">
            <Label className="text-[10px] tracking-[0.1em] uppercase font-bold text-white/60">Acciones automáticas</Label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="printer-cajon-row">
              <Checkbox checked={cfg.abrirCajon} onCheckedChange={(c) => set({ abrirCajon: !!c })} data-testid="printer-cajon"
                className="border-white/40 data-[state=checked]:bg-[#FFCC00] data-[state=checked]:text-[#0A0A0A] data-[state=checked]:border-[#FFCC00]" />
              <span className="text-sm">Abrir cajón de dinero</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="printer-autocorte-row">
              <Checkbox checked={cfg.autocorte} onCheckedChange={(c) => set({ autocorte: !!c })} data-testid="printer-autocorte"
                className="border-white/40 data-[state=checked]:bg-[#FFCC00] data-[state=checked]:text-[#0A0A0A] data-[state=checked]:border-[#FFCC00]" />
              <span className="text-sm">Autocorte de papel</span>
            </label>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <button onClick={imprimirPrueba} disabled={testing} data-testid="printer-test-btn"
              className="btn-hard w-full h-12 border border-[#FFCC00] text-[#FFCC00] font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#FFCC00]/10 disabled:opacity-60">
              {testing ? <Loader2 className="h-5 w-5 animate-spin" /> : <TestTube2 className="h-5 w-5" />} Imprimir ticket de prueba
            </button>
            <button onClick={guardar} data-testid="printer-save-btn"
              className="btn-hard w-full h-12 bg-[#FFCC00] text-[#0A0A0A] border border-[#FFCC00] font-bold uppercase tracking-wider">
              Guardar configuración
            </button>
          </div>
        </div>

        {/* QR oculto: se usa para extraer el data URL en el modo nativo */}
        <div ref={qrRef} className="hidden" aria-hidden><QRCodeCanvas value="PARKNOVA-TEST" size={220} level="M" includeMargin /></div>
      </DialogContent>
    </Dialog>
  );
}
